import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { 
  Flag, Clock, Loader2, RefreshCw, Check, X, Eye, 
  AlertTriangle, MessageSquare, Image as ImageIcon
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const REASON_COLORS = {
  'spam': 'bg-yellow-100 text-yellow-700',
  'harassment': 'bg-red-100 text-red-700',
  'hate_speech': 'bg-red-100 text-red-700',
  'inappropriate': 'bg-orange-100 text-orange-700',
  'violence': 'bg-red-100 text-red-700',
  'misinformation': 'bg-purple-100 text-purple-700',
  'scam': 'bg-amber-100 text-amber-700',
  'other': 'bg-gray-100 text-gray-700'
};

const ReportedContentViewer = ({ groupId }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [processing, setProcessing] = useState({});

  const fetchReports = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await api.get(`/groups/${groupId}/reported-content`, {
        params: { status: filter }
      });
      setReports(res.data.reports || []);
    } catch (error) {
      console.error('Error fetching reported content:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [groupId, filter]);

  const handleAction = async (reportId, action) => {
    setProcessing(prev => ({ ...prev, [reportId]: true }));
    try {
      await api.post(`/groups/${groupId}/reported-content/${reportId}/${action}`);
      toast.success(action === 'approve' ? 'Content removed' : 'Report dismissed');
      setReports(reports.filter(r => r.report_id !== reportId));
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} report`);
    } finally {
      setProcessing(prev => ({ ...prev, [reportId]: false }));
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Flag className="h-5 w-5 text-red-500" />
          Reported Content
        </CardTitle>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border rounded-md px-2 py-1"
          >
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fetchReports(true)}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {reports.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Flag className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No {filter} reports</p>
            <p className="text-sm">Member-reported content will appear here</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-4">
              {reports.map((report) => (
                <div 
                  key={report.report_id} 
                  className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  {/* Report Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className={REASON_COLORS[report.reason] || REASON_COLORS.other}>
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        {report.reason?.replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatRelativeTime(report.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Reported Content Preview */}
                  <div className="bg-white border rounded-lg p-3 mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={report.content_author_image} />
                        <AvatarFallback className="text-xs">
                          {report.content_author_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium">{report.content_author_name}</span>
                    </div>
                    
                    {report.content_type === 'post' && (
                      <div>
                        <p className="text-sm text-gray-700 line-clamp-3">
                          {report.content_preview}
                        </p>
                        {report.content_has_media && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
                            <ImageIcon className="h-3 w-3" />
                            Contains media
                          </div>
                        )}
                      </div>
                    )}
                    
                    {report.content_type === 'comment' && (
                      <div className="flex items-start gap-2">
                        <MessageSquare className="h-4 w-4 text-gray-400 mt-0.5" />
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {report.content_preview}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Reporter Info */}
                  <div className="flex items-center gap-2 mb-3 text-sm text-gray-500">
                    <span>Reported by</span>
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={report.reporter_image} />
                      <AvatarFallback className="text-xs">
                        {report.reporter_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <span>{report.reporter_name}</span>
                  </div>

                  {/* Additional Details */}
                  {report.details && (
                    <p className="text-sm text-gray-600 mb-3 italic">
                      "{report.details}"
                    </p>
                  )}

                  {/* Actions */}
                  {filter === 'pending' && (
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAction(report.report_id, 'dismiss')}
                        disabled={processing[report.report_id]}
                        className="flex-1"
                      >
                        {processing[report.report_id] ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <X className="h-4 w-4 mr-1" />
                            Dismiss
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleAction(report.report_id, 'approve')}
                        disabled={processing[report.report_id]}
                        className="flex-1 bg-red-600 hover:bg-red-700"
                      >
                        {processing[report.report_id] ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Remove Content
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default ReportedContentViewer;
